<script setup lang="ts">
  defineOptions({ name: 'LiveNowCarousel' })

  import { computed, type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { createElementId } from '@common/core-layer/utils'

  import CPButton from '@ui/components/CPButton.vue'
  import CPText from '@ui/components/CPText.vue'

  import LiveNowCard from '../molecules/LiveNowCard.vue'
  import LiveNowCardSkeleton from '../molecules/LiveNowCardSkeleton.vue'
  import SectionEmptyState from '../molecules/SectionEmptyState.vue'
  import { isEventsHubCardLive } from '../../types/events-hub'
  import type { EventsHubCard, EventsHubResourceState } from '../../types/events-hub'

  const props = defineProps({
    state: {
      type: String as PropType<EventsHubResourceState>,
      required: true,
    },
    events: {
      type: Array as PropType<readonly EventsHubCard[]>,
      default: () => [],
    },
    itemLimit: {
      type: Number,
      default: 6,
    },
    total: {
      type: Number,
      default: undefined,
    },
    idPrefix: {
      type: String,
      default: 'live-now-carousel',
    },
  })

  const emit = defineEmits<{
    openEvent: [eventId: string]
    seeAll: []
  }>()

  const { t } = useI18n()
  const SKELETON_CARD_COUNT = 3
  const GRID_CLASS = 'grid grid-cols-[repeat(auto-fill,minmax(min(100%,400px),400px))] gap-4'

  const allLiveEvents = computed(() => props.events.filter(isEventsHubCardLive))
  const liveEvents = computed(() => allLiveEvents.value.slice(0, props.itemLimit))
  const totalCount = computed(() => props.total ?? allLiveEvents.value.length)
  const showSeeAll = computed(() => totalCount.value > props.itemLimit)
  const isVisible = computed(() => props.state === 'loading' || props.state === 'error' || liveEvents.value.length > 0)
  const liveCountLabel = computed(() => {
    const count = totalCount.value
    return count === 1 ? t('clientportal.events.liveCountOne') : t('clientportal.events.liveCountMany', { count })
  })

  const handleOpenEvent = (eventId: string) => {
    emit('openEvent', eventId)
  }

  const handleSeeAll = () => {
    emit('seeAll')
  }
</script>

<template>
  <section v-if="isVisible" :id="createElementId(idPrefix, 'section', 'root')" class="live-now-carousel flex w-full flex-col gap-4">
    <div class="flex items-center gap-2.5">
      <span class="live-now-header-dot" aria-hidden="true" />
      <CPText :id="createElementId(idPrefix, 'section-header', 'title')" size="2xl" weight="bold" html-tag="h2" class="m-0 text-body">
        {{ t('clientportal.events.happeningNow') }}
      </CPText>
      <CPText
        v-if="state === 'ready' && liveEvents.length > 0"
        :id="createElementId(idPrefix, 'section-header', 'count')"
        size="md"
        weight="normal"
        class="text-700"
      >
        {{ liveCountLabel }}
      </CPText>
      <div class="flex-1" />
      <CPButton
        v-if="state === 'ready' && showSeeAll"
        :id="createElementId(idPrefix, 'section-header', 'see-all')"
        color="primary"
        type="text"
        size="sm"
        @click="handleSeeAll"
      >
        {{ t('clientportal.events.seeAll') }}
      </CPButton>
    </div>

    <div
      v-if="state === 'loading'"
      :id="createElementId(idPrefix, 'section', 'loading')"
      :class="GRID_CLASS"
      role="status"
      :aria-label="t('clientportal.events.loadingCardsAriaLabel')"
    >
      <LiveNowCardSkeleton v-for="index in SKELETON_CARD_COUNT" :key="index" :id-prefix="idPrefix" :index="index" />
    </div>

    <SectionEmptyState
      v-else-if="state === 'error'"
      :id="createElementId(idPrefix, 'empty-state', 'error')"
      :title="t('clientportal.events.emptyState.loadErrorTitle')"
      :description="t('clientportal.events.emptyState.loadErrorDescription')"
    />

    <div v-else :id="createElementId(idPrefix, 'section', 'grid')" :class="GRID_CLASS">
      <LiveNowCard v-for="event in liveEvents" :key="event.id" :event="event" :id-prefix="idPrefix" @open-event="handleOpenEvent" />
    </div>
  </section>
</template>

<style scoped>
  .live-now-header-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: var(--error-500, #f04438);
    animation: live-now-header-pulse 1.6s ease-in-out infinite;
  }

  @keyframes live-now-header-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.35;
    }
  }
</style>
